import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { greenButtonClass } from '../../../styling/stylingHelper';
import { useEffect, useState } from 'react';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';
import {
  getAdminUserStats,
  getAllAdminUsers,
  AdminUserStats,
  AdminUserWithStaff,
} from '../../../services/admin/adminService';
import { getUserIdsWithSetting } from '../../../services/admin/superAdminSettingsService';
import { AdminLayout } from './AdminLayout';

const AdminUsersPage = () => {
  const { t } = useTranslation();
  const [userStats, setUserStats] = useState<AdminUserStats | null>(null);
  const [allUsers, setAllUsers] = useState<AdminUserWithStaff[]>([]);
  const [demoUserIds, setDemoUserIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedAdmins, setExpandedAdmins] = useState<Set<string>>(new Set());

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [stats, all, demoIds] = await Promise.all([
          getAdminUserStats(),  // For stats (excludes demo)
          getAllAdminUsers(),   // For table (includes demo)
          getUserIdsWithSetting('demo_account'),  // Get demo account user IDs
        ]);
        setUserStats(stats);
        setAllUsers(all.adminUsers);
        setDemoUserIds(demoIds);
      } catch (error) {
        console.error('Failed to load user stats:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const toggleAdminExpanded = (adminId: string) => {
    setExpandedAdmins(prev => {
      const next = new Set(prev);
      if (next.has(adminId)) {
        next.delete(adminId);
      } else {
        next.add(adminId);
      }
      return next;
    });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className={cn(secondaryText)}>Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex justify-end mb-5"><Link className={`${greenButtonClass} min-h-[44px]`} to="/admin/customers/new">{t('managedSignup.newCustomer')}</Link></div>
      {/* Summary Stats */}
      {(() => {
        // Compute stats from allUsers so they add up correctly
        const totalAdmins = allUsers.length;
        const totalStaff = allUsers.reduce((sum, a) => sum + a.staff.length, 0);
        const totalAll = totalAdmins + totalStaff;

        const demoAdmins = allUsers.filter(a => demoUserIds.includes(a.id));
        const demoAdminCount = demoAdmins.length;
        const demoStaffCount = demoAdmins.reduce((sum, a) => sum + a.staff.length, 0);
        const totalDemo = demoAdminCount + demoStaffCount;

        const realAdmins = totalAdmins - demoAdminCount;
        const realStaff = totalStaff - demoStaffCount;
        const totalReal = totalAll - totalDemo;

        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <div className={cn(cardClass, 'p-4')}>
              <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Total Users</div>
              <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
                {totalAll}
              </div>
              <div className={cn(secondaryText, 'text-xs')}>{totalAdmins} admin + {totalStaff} staff</div>
            </div>
            <div className={cn(cardClass, 'p-4')}>
              <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Real Users</div>
              <div className={cn('text-2xl', semiBoldText, 'text-green-600 dark:text-green-400')}>
                {totalReal}
              </div>
              <div className={cn(secondaryText, 'text-xs')}>{realAdmins} admin + {realStaff} staff</div>
            </div>
            <div className={cn(cardClass, 'p-4')}>
              <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Demo Users</div>
              <div className={cn('text-2xl', semiBoldText, 'text-orange-500 dark:text-darkOrangeColor-dark')}>
                {totalDemo}
              </div>
              <div className={cn(secondaryText, 'text-xs')}>{demoAdminCount} admin + {demoStaffCount} staff</div>
            </div>
            <div className={cn(cardClass, 'p-4')}>
              <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Admin Accounts</div>
              <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
                {totalAdmins}
              </div>
            </div>
            <div className={cn(cardClass, 'p-4')}>
              <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Staff Users</div>
              <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
                {totalStaff}
              </div>
            </div>
            <div className={cn(cardClass, 'p-4')}>
              <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Active Subscriptions</div>
              <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
                {userStats?.activeSubscriptions ?? 0}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Users Table */}
      <div className={cn(cardClass, 'overflow-hidden')}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className={cn(primaryText, semiBoldText)}>All Users</h2>
          <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
            Click on a row to expand and see staff members
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className={cn('text-left p-3', secondaryText, smallTextSize, semiBoldText)}>Email</th>
                <th className={cn('text-left p-3', secondaryText, smallTextSize, semiBoldText)}>Name</th>
                <th className={cn('text-left p-3', secondaryText, smallTextSize, semiBoldText)}>Status</th>
                <th className={cn('text-left p-3', secondaryText, smallTextSize, semiBoldText)}>Created</th>
                <th className={cn('text-left p-3', secondaryText, smallTextSize, semiBoldText)}>Staff</th>
              </tr>
            </thead>
            <tbody>
              {allUsers.map(admin => {
                const isDemo = demoUserIds.includes(admin.id);
                return (
                <>
                  <tr
                    key={admin.id}
                    className={cn(
                      'border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-colors',
                      expandedAdmins.has(admin.id)
                        ? 'bg-purple-50 dark:bg-purple-900/10'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800/50',
                      isDemo && 'bg-orange-50/50 dark:bg-orange-900/10'
                    )}
                    onClick={() => toggleAdminExpanded(admin.id)}
                  >
                    <td className={cn('p-3', primaryText, smallTextSize)}>
                      <span className="flex items-center gap-2">
                        {admin.email}
                        {isDemo && (
                          <span className="px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
                            Demo
                          </span>
                        )}
                      </span>
                    </td>
                    <td className={cn('p-3', primaryText, smallTextSize)}>{admin.name || '-'}</td>
                    <td className={cn('p-3', smallTextSize)}>
                      <span
                        className={cn(
                          'px-2 py-1 rounded-full text-xs font-medium',
                          admin.subscription_status === 'ACTIVE'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        )}
                      >
                        {admin.subscription_status}
                      </span>
                    </td>
                    <td className={cn('p-3', secondaryText, smallTextSize)}>{formatDate(admin.created_at)}</td>
                    <td className={cn('p-3', primaryText, smallTextSize)}>
                      <span className="flex items-center gap-2">
                        {admin.staff.length}
                        {admin.staff.length > 0 && (
                          <span className={cn('text-xs transition-transform', expandedAdmins.has(admin.id) ? 'rotate-90' : '')}>
                            ▶
                          </span>
                        )}
                      </span>
                    </td>
                  </tr>
                  {expandedAdmins.has(admin.id) && admin.staff.length > 0 && (
                    admin.staff.map(staff => (
                      <tr
                        key={staff.id}
                        className="bg-gray-50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800"
                      >
                        <td className={cn('p-3 pl-8', secondaryText, smallTextSize)}>
                          <span className="flex items-center gap-2">
                            <span className="text-gray-300 dark:text-gray-600">└</span>
                            {staff.email}
                          </span>
                        </td>
                        <td className={cn('p-3', secondaryText, smallTextSize)}>{staff.name || '-'}</td>
                        <td className={cn('p-3', smallTextSize)}>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            Staff
                          </span>
                        </td>
                        <td className={cn('p-3', secondaryText, smallTextSize)}>{formatDate(staff.created_at)}</td>
                        <td className="p-3"></td>
                      </tr>
                    ))
                  )}
                </>
              );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUsersPage;
